/**
 * LA LECTURE DE LA GARDE SERVEUR `ALARMES_V2`, TENUE PAR ENTREPRISE.
 * ============================================================================
 *
 * INTENTION : le front n'a pas d'interrupteur à lui. Il lit la décision du
 * seul endroit qui fait foi, la garde serveur d'api-metier. Il la lit par
 * `GET /alarmes-v2/ouverture`, qui rend toujours 200 `{ouvert: true|false}`
 * (401 sans session), avec le même jugement que la garde (`estOuvertPour`),
 * sans appeler le service d'alarmes : un compte hors de la population ne
 * produit donc AUCUN refus, et le suivi des erreurs ne reçoit rien, même le
 * jour où le module est fermé pour tous en production.
 *
 * Deux temps :
 *  1. `/ouverture`. `ouvert: false` : le module est fermé pour la session, et
 *     `/offre` n'est JAMAIS appelée. `ouvert: true` : le module est ouvert.
 *     Toute autre issue n'est pas une réponse : une panne (5xx, réseau coupé,
 *     aucun statut), un refus passager (408, 429), un 401 (la session, pas
 *     l'ouverture : `invalid_auth` déconnecte déjà, par `axios.plugin.ts`), un
 *     autre 4xx, ou un 200 sans booléen `ouvert` (réponse illisible). Le
 *     module reste alors fermé SANS le retenir, et l'appel suivant relit.
 *     « Je n'ai pas pu savoir » n'est pas « fermé pour ce compte ».
 *  2. Seulement si le module est ouvert, `/offre` : le niveau, l'essai, le
 *     solde. Si elle échoue (ou rend une offre illisible), le module reste
 *     OUVERT et l'offre est « non lue » (`offreLue` faux, `offre` nulle) :
 *     l'abonnement n'est pas su, et `useAcces` rend alors tout geste absent,
 *     ni montré actif à tort, ni fermé à tort par un solde supposé. L'offre
 *     non lue est relue à la prochaine entrée sur une page du module
 *     (`charger({ relireOffre: true })`, par la porte), jamais à chaque
 *     navigation de l'application.
 *
 * Patron de `core/stores/interrupteurs.store.ts` (idempotent, appel partagé,
 * ne rejette jamais, fermé par défaut), à une différence près : la réponse est
 * propre à la personne et à l'entreprise de la session. Le store retient donc
 * la clé lue (`cleLue`, et `societeLue`) :
 *  • même clé, réponse reçue : rien n'est relu ;
 *  • autre clé : fermé tout de suite, offre vidée, puis relu ;
 *  • aucune entreprise (déconnexion) : fermé, sans appel ;
 *  • une réponse arrivée pour une session qui n'est plus la courante est
 *    jetée, en succès comme en échec, sans toucher à l'état.
 * `root.store.ts` n'a pas à le connaître : la clé suffit.
 *
 * Ce store CACHE le module, il ne le protège pas : seule la garde serveur ferme.
 */
import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { useAuthStore } from "@core/stores/auth.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import { abonnementCourant } from "@modules/alarmes-v2/acces/acces"
import type {
    AlarmesV2Abonnement,
    AlarmesV2Offre,
} from "@modules/alarmes-v2/types/alarmes-v2"

/**
 * La clé de la session : la personne ET l'entreprise. La garde serveur peut
 * nommer un courriel, et le service filtre ce que chacun lit : deux personnes
 * de la même entreprise, l'une après l'autre dans le même onglet, ne partagent
 * rien. `""` sans entreprise (déconnexion).
 */
export function cleDeSession(): string {
    const auth = useAuthStore()
    return auth.companyId ? `${auth.userId}|${auth.companyId}` : ""
}

/**
 * La réponse de `/ouverture` : `true` ou `false` quand elle est lue, `null`
 * quand on n'a pas pu savoir (échec de l'appel, ou 200 sans booléen `ouvert`).
 */
async function lireOuverture(): Promise<boolean | null> {
    try {
        const reponse = await serviceAlarmesV2.alarmesV2LireOuverture()
        return typeof reponse?.ouvert === "boolean" ? reponse.ouvert : null
    } catch {
        return null
    }
}

/**
 * L'offre servie, ou `null` quand elle n'a pas pu être lue : échec de l'appel,
 * ou niveau absent ou inconnu (une offre illisible n'est pas un Basique).
 * Un `sms` absent vaut `null` (solde inconnu), jamais un solde à 0.
 */
async function lireOffre(): Promise<AlarmesV2Offre | null> {
    try {
        const offre = await serviceAlarmesV2.alarmesV2LireOffre()
        if (offre?.niveau !== "basique" && offre?.niveau !== "avance")
            return null
        return { ...offre, sms: offre.sms ?? null }
    } catch {
        return null
    }
}

/** Les options d'une lecture. */
export interface OptionsCharger {
    /**
     * Relire l'offre si elle n'a pas pu être lue alors que le module est
     * ouvert. Posé par la porte des pages du module, jamais par le menu.
     */
    relireOffre?: boolean
}

export const useOuvertureStore = defineStore("alarmesV2Ouverture", () => {
    /** Vrai seulement quand `/ouverture` a répondu `ouvert: true` pour la session. */
    const ouvert = ref(false)

    /** L'offre servie par `/offre`, `null` tant qu'elle n'est pas lue. */
    const offre = ref<AlarmesV2Offre | null>(null)

    /** Vrai quand l'offre a été lue pour la session courante. */
    const offreLue = ref(false)

    /** L'entreprise pour laquelle `/ouverture` a répondu, `""` sinon. */
    const societeLue = ref("")

    /** La clé de session de cette lecture (`cleDeSession`), `""` sinon. */
    const cleLue = ref("")

    /** La lecture en vol et sa clé, partagées par tous les appelants. */
    let enVol: Promise<void> | null = null
    let enVolPour = ""

    /** Repasse à l'état de départ : fermé, sans offre, rien de lu. */
    function fermer(): void {
        ouvert.value = false
        offre.value = null
        offreLue.value = false
        societeLue.value = ""
        cleLue.value = ""
    }

    /**
     * Lit l'ouverture pour la session courante, une fois, puis l'offre si le
     * module est ouvert. Ne rejette jamais.
     */
    async function charger(options: OptionsCharger = {}): Promise<void> {
        const societe = useAuthStore().companyId
        const cle = cleDeSession()
        if (!societe) {
            fermer()
            enVol = null
            enVolPour = ""
            return
        }
        if (enVol && enVolPour === cle) return enVol
        const ouvertureLue = cleLue.value === cle
        if (ouvertureLue && !ouvert.value) return
        if (ouvertureLue && (offreLue.value || !options.relireOffre)) return

        // Une autre session : rien de la précédente ne reste visible.
        if (!ouvertureLue) fermer()
        enVolPour = cle
        const lecture = (async () => {
            if (!ouvertureLue) {
                const reponse = await lireOuverture()
                // Fin de lecture d'une session qui n'est plus la courante : jetée.
                if (cleDeSession() !== cle) return
                // Pas de réponse : fermé, rien de retenu, la suivante relit.
                if (reponse === null) return
                societeLue.value = societe
                cleLue.value = cle
                // Fermé pour ce compte : l'offre n'est pas demandée.
                if (!reponse) return
                ouvert.value = true
            }
            const lue = await lireOffre()
            if (cleDeSession() !== cle) return
            offre.value = lue
            offreLue.value = lue !== null
        })()
        enVol = lecture
        try {
            await lecture
        } finally {
            if (enVol === lecture) {
                enVol = null
                enVolPour = ""
            }
        }
    }

    /** Vrai quand la lecture de l'ouverture vaut pour la session courante. */
    const luPourLaSession = computed(
        (): boolean => cleLue.value !== "" && cleLue.value === cleDeSession()
    )

    /**
     * L'abonnement tel que la matrice des accès le lit (plan §2.4, point 6),
     * ou `null` quand il n'est pas su : module fermé, lecture d'une autre
     * session, ou offre non lue pour une entreprise de l'offre Energy Manager.
     * Un solde de SMS inconnu (`sms: null`) ne ferme rien.
     */
    const abonnement = computed((): AlarmesV2Abonnement | null => {
        if (!luPourLaSession.value || !ouvert.value) return null
        return abonnementCourant({
            hasOfferEnergyManager: useAuthStore().hasOfferEnergyManager,
            offre: offre.value,
        })
    })

    /** Remet le store à son état de départ (tests, stories). */
    function reinitialiser(): void {
        fermer()
        enVol = null
        enVolPour = ""
    }

    return {
        ouvert,
        offre,
        offreLue,
        societeLue,
        luPourLaSession,
        abonnement,
        charger,
        reinitialiser,
    }
})
